<script>
import ContentBox from '@/components/ui/ContentBox';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import ImageInput from '@/components/form-controls/ImageInput';
import RequirePlan from '@/components/ui/RequirePlan';
import getErrorMessage from '@/modules/server-errors';
import latinize from 'latinize';

export default {
  name: 'Settings',
  components: {
    ContentBox,
    DashboardLayout,
    ImageInput,
    RequirePlan
  },
  metaInfo() {
    return {
      title: this.title
    };
  },
  data() {
    const account = this.$store.state.user.account;
    return {
      title: this.$t('Settings'),
      breadcrumbs: [{ text: this.$t('Settings') }],
      account: {},
      activeTab: 'branding',
      cleanState: {},
      deleteDialogVisible: false,
      deleting: false,
      form: {
        appName: account.appName,
        subdomain: account.subdomain,
        supportUrl: account.supportUrl,
        logoLight: account.logoLight,
        logoDark: account.logoDark,
        favicon: account.favicon,
        gaAuthToken: account.gaAuthToken,
        requiresTfa: account.requiresTfa,
        customLogoutUrl: account.customLogoutUrl,
        customLoginsEnabled: account.customLoginsEnabled
      },
      invalid: [],
      isDisabled: false,
      isSaving: false,
      isResettingDemo: false,
      ownerPassword: ''
    };
  },
  computed: {
    // Whether the current user may toggle custom logins (gated to the EZLynx account owner; June 2026)
    isCustomLoginsOwner() {
      const user = this.$store.state.user;
      return user.isOwner && user.account.id === '8a8abb87-6695-427b-ae94-f36ab137c5fe';
    },

    // Set GA switch value based on token existence
    gaEnabled: {
      get() {
        return this.form.gaAuthToken !== null;
      },
      set() {
        // We don't set anything here, it's just required for v-model
        return;
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  created() {
    this.resetCleanState();
  },
  mounted() {
    this.$refs.appName.focus();
  },
  methods: {
    // Returns the form's data
    getData() {
      return Object.assign({}, this.form);
    },

    // Sets the form's data
    setData(data) {
      Object.keys(this.form).forEach(key => {
        this.form[key] = typeof data[key] === 'undefined' ? this.form[key] : data[key];
      });
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    },

    // Deletes the account
    handleDelete(password) {
      this.deleting = true;
      this.$http
        .delete('/account', {
          data: { password }
        })
        .then(() => {
          this.$store.commit('unsetUser');
          this.resetCleanState();
          location.href = 'https://www.surrealcms.com/';
        })
        .catch(err => {
          this.invalid = err.response.data.invalid;
          this.$notify({
            type: 'error',
            title: this.$t('Delete'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.deleting = false));
    },

    // Saves the settings
    handleSave() {
      const data = this.getData();
      this.isSaving = true;
      this.$http
        .put('/account', data)
        .then(res => {
          this.invalid = [];
          this.$store.commit('setAccount', res.data.account);
          this.setData(res.data.account);
          this.resetCleanState();
        })
        // Update metadata
        .then(() => this.$http('/auth/session'))
        .then(res => this.$store.commit('setMetadata', res.data.metadata))
        .then(() => {
          this.$notify({
            type: 'success',
            title: this.title,
            message: this.$t('Your changes have been saved.')
          });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    },

    // Confirms turning custom logins off. Copy is intentionally raw (not translated) per request.
    // Nothing is persisted until the user saves; this just warns them and lets them back out.
    handleCustomLoginsChange(enabled) {
      // Only confirm when switching the feature off
      if (enabled) {
        return;
      }

      this.$confirm(
        'Turning off custom logins will restore standard sign-in for non-admin users on this account. They will be able to change and reset their own passwords again, and two-factor authentication will be required for them if it is enabled for the account. This takes effect after you save your changes.',
        'Turn off custom logins?',
        {
          confirmButtonText: 'Keep Custom Logins On',
          cancelButtonText: 'Turn Off'
        }
      )
        .then(() => {
          // Default call to action: keep the feature on and re-toggle the switch
          this.form.customLoginsEnabled = true;
        })
        .catch(action => {
          // Only turn it off when explicitly chosen; dismissing (Esc/close) keeps it on
          if (action !== 'cancel') {
            this.form.customLoginsEnabled = true;
          }
        });
    },

    // Format the subdomain when it changes
    handleSubdomainChange() {
      this.form.subdomain = latinize(this.form.subdomain)
        .toLowerCase()
        .replace(/[\s|_]+/g, '-') // convert spaces and underscores to dashes
        .replace(/[^a-z0-9-]/g, '') // remove invalid characters
        .replace(/-+/g, '-') // remove duplicate dashes
        .replace(/^-+/, '') // strip starting dashes
        .replace(/-+$/, ''); // strip ending dashes
    },

    // Resets the demo site
    resetDemo() {
      this.isResettingDemo = true;
      this.$http
        .post('/account/demo')
        .then(() => {
          this.$notify({
            type: 'success',
            title: this.$t('Demo Site'),
            message: this.$t('The demo has been reset.')
          });
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isResettingDemo = false));
    },

    // Toggles the Google analytics service
    toggleGA(enabled) {
      if (enabled) {
        const win = window.open('about:blank'); // avoid popup blockers
        this.form.gaAuthToken = null;
        this.$http
          .get('/services/ga/oauth')
          // Open the GA oAuth page
          .then(res => {
            win.location = res.data.url;

            // Wait for the OAuth callback
            window.addEventListener('gaOauthComplete', event => {
              this.form.gaAuthToken = event.detail.authToken;
            });
          })
          .catch(err => {
            win.close();
            this.form.gaAuthToken = null;
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage(err.response.data.code)
            });
          });
      } else {
        // Disable the service
        this.form.gaAuthToken = null;
      }
    }
  }
};
</script>

<template>
  <dashboard-layout size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <el-form
      ref="settingsForm"
      :disabled="isDisabled"
      class="settings-form"
      label-position="top"
      @submit.native.prevent="handleSave"
    >
      <content-box>
        <el-tabs v-model="activeTab">
          <!-- Branding -->
          <el-tab-pane :label="$t('Branding')" name="branding">
            <fieldset>
              <h2>
                {{ $t('White-label Settings') }}
                <require-plan plan="agency" />
              </h2>
              <p>
                {{
                  $t(
                    "Give your clients a custom solution with your company's branding. Clients will see your business, your logo, and your custom domain."
                  )
                }}
              </p>

              <!-- Subdomain -->
              <el-form-item :class="{ 'is-error': invalid.includes('subdomain') }" :label="$t('Domain')">
                <el-input
                  v-model="form.subdomain"
                  :disabled="$store.state.user.account.plan !== 'agency' || $store.state.metadata.isCustomDomain"
                  type="text"
                  auto-complete="off"
                  class="subdomain-input"
                  @change="handleSubdomainChange"
                >
                  <template slot="append"> .clientcms.com </template>
                </el-input>
                <div v-if="$store.state.metadata.isCustomDomain" class="text-form-description">
                  {{ $t("This cannot be changed when you're logged in to your custom domain.") }}
                </div>
                <div v-else class="text-form-description">
                  {{ $t('White-label settings will be applied when you login in to your custom domain.') }}
                </div>
              </el-form-item>

              <!-- Show warning when changing the subdomain -->
              <el-alert
                v-if="
                  $store.state.user.account.subdomain !== '' && $store.state.user.account.subdomain !== form.subdomain
                "
                :closable="false"
                :title="$t('Warning')"
                :description="
                  $t(
                    'Changing this will cause all active users to be logged out immediately. You are responsible for informing users of the new domain.'
                  )
                "
                class="white-label-warning"
                type="warning"
              />

              <!-- App name -->
              <el-form-item :class="{ 'is-error': invalid.includes('appName') }" :label="$t('App Name')">
                <el-input
                  ref="appName"
                  v-model="form.appName"
                  :disabled="$store.state.user.account.plan !== 'agency'"
                  type="text"
                  auto-complete="off"
                  placeholder="Surreal CMS"
                />
                <div class="text-form-description">
                  {{ $t('This will be displayed instead of "Surreal CMS" throughout the app.') }}
                </div>
              </el-form-item>

              <!-- Support URL -->
              <el-form-item :class="{ 'is-error': invalid.includes('supportUrl') }" :label="$t('Support URL')">
                <el-input
                  v-model="form.supportUrl"
                  :disabled="$store.state.user.account.plan !== 'agency'"
                  type="url"
                  auto-complete="off"
                  prefix-icon="el-icon-fa-link"
                />
                <div class="text-form-description">
                  {{ $t('If set, clients will be sent to this URL instead of the standard support page.') }}
                </div>
              </el-form-item>
            </fieldset>

            <fieldset>
              <el-row :gutter="20">
                <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                  <!-- Logo light -->
                  <el-form-item :label="$t('Light Logo')">
                    <image-input
                      v-model="form.logoLight"
                      :value="form.logoLight"
                      :default-image="require('@/assets/wordmark-light.svg')"
                      :disabled="$store.state.user.account.plan !== 'agency'"
                      :height="128"
                    />
                    <div class="text-form-description">
                      {{ $t('Select a logo to use on light backgrounds.') }}
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                  <!-- Logo dark -->
                  <el-form-item :label="$t('Dark Logo')">
                    <image-input
                      v-model="form.logoDark"
                      :value="form.logoDark"
                      :default-image="require('@/assets/wordmark-dark.svg')"
                      :disabled="$store.state.user.account.plan !== 'agency'"
                      :height="128"
                      dark
                    />
                    <div class="text-form-description">
                      {{ $t('Select a logo to use on dark backgrounds.') }}
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                  <!-- Favicon -->
                  <el-form-item :label="$t('Favicon')">
                    <image-input
                      v-model="form.favicon"
                      :value="form.favicon"
                      :crop-aspect-ratio="1"
                      :default-image="require('@/assets/favicon.png')"
                      :disabled="$store.state.user.account.plan !== 'agency'"
                      :height="128"
                      image-max-width="32px"
                      image-max-height="32px"
                      crop
                    />
                    <div class="text-form-description">
                      {{ $t('Select an image to use as the favicon.') }}
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </fieldset>
          </el-tab-pane>

          <!-- Services -->
          <el-tab-pane :label="$t('Services')" name="services">
            <fieldset>
              <!-- Google Analytics -->
              <h2>
                {{ $t('Analytics') }}
                <require-plan :plan="['business', 'agency']" />
              </h2>
              <p>
                {{ $t('Connect your Google Analytics account to enable the analytics dashboard for selected sites.') }}
              </p>
              <el-form-item>
                <el-switch
                  v-model="gaEnabled"
                  :disabled="
                    !['business', 'agency'].includes($store.state.user.account.plan) ||
                    $store.state.metadata.isCustomDomain
                  "
                  :style="{ 'margin-right': '.25rem' }"
                  active-text="Google Analytics"
                  auto-complete="off"
                  @input="toggleGA($event)"
                />
                <div v-if="$store.state.metadata.isCustomDomain" class="text-form-description">
                  {{ $t("This cannot be changed when you're logged in to your custom domain.") }}
                </div>
              </el-form-item>
            </fieldset>
          </el-tab-pane>

          <!-- Advanced -->
          <el-tab-pane v-if="$store.state.user.isOwner" :label="$t('Advanced')" name="advanced">
            <!-- Two-factor authentication -->
            <fieldset>
              <h2>{{ $t('Require Two-factor Authentication') }}</h2>
              <p>
                {{
                  $t(
                    'This will require all users to enable two-factor authentication. Every user will need to use an app such as Google Authenticator before they can access their account.'
                  )
                }}
              </p>
              <p v-if="!$store.state.user.requiresTfa">
                {{
                  $t('You must enable two-factor authentication for your own account before requiring it for everyone.')
                }}
                <router-link :to="{ name: 'EnableTfa' }">
                  {{ $t('Enable') }}
                </router-link>
              </p>
              <el-form-item>
                <el-switch
                  v-model="form.requiresTfa"
                  :active-text="$t('Require for all users')"
                  :disabled="!$store.state.user.requiresTfa"
                />
              </el-form-item>
            </fieldset>

            <hr />

            <!-- Custom logout URL -->
            <fieldset>
              <h2>{{ $t('Custom Logout URL') }}</h2>
              <p>
                {{ $t('If set, users will be redirected to this URL after they log out.') }}
              </p>
              <el-form-item :class="{ 'is-error': invalid.includes('customLogoutUrl') }">
                <el-input
                  v-model="form.customLogoutUrl"
                  type="url"
                  auto-complete="off"
                  prefix-icon="el-icon-fa-link"
                  placeholder="https://"
                />
              </el-form-item>
            </fieldset>

            <!-- Custom logins (restricted) -->
            <template v-if="isCustomLoginsOwner">
              <hr />

              <fieldset>
                <h2>{{ $t('Custom Logins') }}</h2>
                <p>
                  {{
                    $t(
                      'When enabled, users who are not administrators sign in through your own system and cannot change or reset their password within the CMS. Administrators are unaffected.'
                    )
                  }}
                </p>
                <el-form-item>
                  <el-switch
                    v-model="form.customLoginsEnabled"
                    :active-text="$t('Enable custom logins for non-admins')"
                    @change="handleCustomLoginsChange"
                  />
                </el-form-item>
              </fieldset>
            </template>

            <hr />

            <!-- Demo -->
            <fieldset>
              <h2>{{ $t('Demo Site') }}</h2>
              <p>
                {{
                  $t(
                    "We provide a demo site for you to experiment with. If you've modified it or removed it, you can reset it here."
                  )
                }}
              </p>
              <el-form-item>
                <el-button :loading="isResettingDemo" type="default" @click="resetDemo">
                  {{ $t('Reset Demo') }}
                </el-button>
              </el-form-item>
            </fieldset>

            <hr />

            <!-- Delete -->
            <fieldset>
              <h2>{{ $t('Delete My Account') }}</h2>
              <p>
                {{ $t('This will delete your account permanently.') }}
                <i18n
                  v-if="['business', 'agency'].includes($store.state.user.account.plan)"
                  :path="$t('To change your subscription, please visit the {billing} section.')"
                  tag="span"
                >
                  <template v-slot:billing>
                    <router-link :to="{ name: 'Billing' }">
                      {{ $t('Billing') }}
                    </router-link>
                  </template>
                </i18n>
              </p>
              <el-form-item>
                <el-button type="danger" @click="deleteDialogVisible = true">
                  {{ $t('Delete My Account') }}
                </el-button>
              </el-form-item>
            </fieldset>
          </el-tab-pane>
        </el-tabs>
      </content-box>

      <div class="text-right">
        <!-- Submit -->
        <el-button :loading="isSaving" type="primary" native-type="submit">
          {{ $t('Save Changes') }}
        </el-button>
      </div>
    </el-form>

    <!-- Delete account dialog -->
    <el-dialog
      :title="$t('Delete My Account')"
      :visible.sync="deleteDialogVisible"
      @opened="$refs.ownerPassword.focus()"
      @close="ownerPassword = ''"
    >
      <p>
        {{ $t('Once your account is deleted, it cannot be recovered!') }}
      </p>
      <el-form>
        <el-form-item
          :label="$t('Please enter your password to continue.')"
          :class="{ 'is-error': invalid.includes('password') }"
        >
          <el-input
            ref="ownerPassword"
            v-model="ownerPassword"
            prefix-icon="el-icon-fa-lock"
            type="password"
            auto-complete="off"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">{{ $t('Cancel') }}</el-button>
        <el-button :loading="deleting" type="danger" @click="handleDelete(ownerPassword)">
          {{ $t('Delete My Account') }}
        </el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.white-label-warning {
  margin-bottom: 1.5rem;
}
</style>
